import React, { useState, useMemo, useEffect } from 'react';
import { PlusCircle, Edit2, Trash2, Download, Paperclip } from 'lucide-react';

import { DateRangeFilter } from '../../components/ui/DateRangeFilter';
import { LiabilityModal } from './LiabilityModal';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Pagination } from '../../components/ui/Pagination';
import { ActionMenu, ActionMenuItem } from '../../components/ui/ActionMenu';
import { AttachmentViewerModal } from '../../components/ui/AttachmentViewerModal';
import { formatCurrency, formatDateForDisplay, parseAttachmentUrls, exportToExcel } from '../../utils/helpers';

const ITEMS_PER_PAGE = 20;

export const Liabilities = ({ liabilities, onSaveLiability, onDeleteLiability, onUpdateLiabilityStatus, fileHandler }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingLiability, setEditingLiability] = useState(null);
    const [deletingLiability, setDeletingLiability] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [viewingAttachments, setViewingAttachments] = useState({isOpen: false, urls: []});
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const sortedLiabilities = useMemo(() => [...liabilities].sort((a,b) => new Date(b.date) - new Date(a.date)), [liabilities]);

     const filteredLiabilities = useMemo(() => {
        return sortedLiabilities 
            .filter(l => {
                if (!startDate || !endDate) return true;
                const liabilityDate = new Date(l.date);
                const start = new Date(startDate);
                start.setHours(0,0,0,0);
                const end = new Date(endDate);
                end.setHours(23,59,59,999);
                return liabilityDate >= start && liabilityDate <= end;
            })
    }, [sortedLiabilities, startDate, endDate]);

    const paginatedLiabilities = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredLiabilities.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredLiabilities, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredLiabilities]);

    const handleSave = async (liabilityData, file, isEditing) => {
        await onSaveLiability(liabilityData, file, isEditing);
        setIsModalOpen(false);
        setEditingLiability(null);
    };

    const handleEdit = (liability) => {
        setEditingLiability(liability);
        setIsModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        onDeleteLiability(deletingLiability.id);
        setDeletingLiability(null);
    };

    const handleExport = () => {
        const dataToExport = filteredLiabilities.map(l => ({
            'Mã Phiếu': l.id,
            'Ngày Tạo': formatDateForDisplay(l.date),
            'Loại': l.type,
            'Đối Tượng': l.subject,
            'Số Tiền': l.amount,
            'Hạn Thanh Toán': l.dueDate ? formatDateForDisplay(l.dueDate) : '',
            'Trạng Thái': l.status,
            'Đính Kèm': l.attachmentUrl || ''
        }));
        exportToExcel(dataToExport, 'SoCongNo');
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-gray-800">Sổ Công Nợ</h2>
                <div className="flex gap-2">
                    <button onClick={handleExport} className="flex items-center gap-2 bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:bg-green-700 transition">
                        <Download size={20} /> Xuất Excel
                    </button>
                    <button onClick={() => { setEditingLiability(null); setIsModalOpen(true); }} className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:bg-blue-700 transition">
                        <PlusCircle size={20} /> Tạo Phiếu Công Nợ
                    </button>
                </div>
            </div>

             <DateRangeFilter startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} />
            
            {viewingAttachments.isOpen && (
                <AttachmentViewerModal 
                    urls={viewingAttachments.urls} 
                    onClose={() => setViewingAttachments({isOpen: false, urls: []})} 
                    onOpenFile={fileHandler.open} 
                />
             )}
             <LiabilityModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} existingLiability={editingLiability}/>
             <ConfirmationModal 
                isOpen={!!deletingLiability} 
                onClose={() => setDeletingLiability(null)} 
                onConfirm={handleDeleteConfirm} 
                title="Xác nhận Xóa Công Nợ"
                message={`Bạn có chắc muốn xóa phiếu công nợ cho ${deletingLiability?.subject} (${formatCurrency(deletingLiability?.amount)})?`} 
            />

            <div className="bg-white rounded-lg shadow-md">
                <div className="overflow-x-auto p-6">
                       <table className="w-full text-left">
                            <thead className="border-b bg-gray-50">
                                <tr>
                                    {['Ngày tạo', 'Loại', 'Đối tượng', 'Hạn TT', 'Số tiền', 'Trạng thái', 'Đính kèm', 'Hành động'].map(h => <th key={h} className="p-4 font-semibold text-gray-600">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedLiabilities.map(l => {
                                    const attachments = parseAttachmentUrls(l.attachmentUrl);
                                    return (
                                    <tr key={l.id} className="border-b hover:bg-gray-50">
                                        <td className="p-4">{formatDateForDisplay(l.date)}</td>
                                        <td className={`p-4 font-medium ${l.type === 'Phải thu' ? 'text-blue-600' : 'text-orange-600'}`}>{l.type}</td>
                                        <td className="p-4">{l.subject}</td>
                                        <td className="p-4">{l.dueDate ? formatDateForDisplay(l.dueDate) : ''}</td>
                                        <td className="p-4 font-semibold text-right">{formatCurrency(l.amount)}</td>
                                        <td className="p-4">
                                            <button onClick={() => onUpdateLiabilityStatus(l.id, l.status === 'Chưa thanh toán' ? 'Đã thanh toán' : 'Chưa thanh toán')} className={`px-3 py-1 text-sm text-white font-semibold rounded-full ${l.status === 'Đã thanh toán' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}>
                                                {l.status}
                                            </button>
                                        </td>
                                        <td className="p-4 text-center">
                                            {attachments.length > 0 && (
                                                <button onClick={() => setViewingAttachments({isOpen: true, urls: attachments})} className="flex items-center gap-1.5 text-blue-600 hover:underline">
                                                    <Paperclip size={16} />
                                                    ({attachments.length})
                                                </button>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <ActionMenu>
                                                <ActionMenuItem icon={<Edit2 size={16} />} label="Sửa phiếu" onClick={() => handleEdit(l)} />
                                                <ActionMenuItem icon={<Trash2 size={16} />} label="Xóa phiếu" onClick={() => setDeletingLiability(l)} />
                                            </ActionMenu>
                                        </td>
                                    </tr>
                                )})}
                            </tbody>
                        </table>
                </div>
                 <Pagination
                    currentPage={currentPage}
                    totalItems={filteredLiabilities.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                />
            </div>
        </div>
    );
};